import { Check } from 'lucide-react';
import * as React from 'react';

import type { BrandIconId } from '../../assets/brandIcons';
import { ConnectedServiceIcon } from '../../atoms/ConnectedServiceIcon';
import { cn } from '../../lib/utils';

import { WizardPanel, type WizardPanelProps } from './WizardPanel';

/** The three selection groups, keyed by the saved-progress field they fill. */
type SectionKey = 'idp' | 'workspace' | 'hrSystem';

interface EnvOption {
  id: string;
  brand: BrandIconId;
  label: string;
}

/**
 * The step's sections and tiles, in Figma's order (`2754:8369`). Option ids
 * match the wire enums in @dawn/feature-config (`ONBOARDING_ENV_*`) — unique
 * across sections, so a flat `disabledOptions` list suffices.
 */
const SECTIONS: ReadonlyArray<{
  key: SectionKey;
  label: string;
  required: boolean;
  description: string;
  options: readonly EnvOption[];
}> = [
  {
    key: 'idp',
    label: 'Identity provider',
    required: true,
    description:
      "The directory Dawn reads read-only to map who's who — separate from the SSO you use to sign in to Dawn.",
    options: [
      { id: 'entra-id', brand: 'entra-id', label: 'Entra ID' },
      { id: 'google', brand: 'google-workspace', label: 'Google IdP' },
      { id: 'okta', brand: 'okta-ink', label: 'Okta' },
    ],
  },
  {
    key: 'workspace',
    label: 'Workspace',
    required: true,
    description: 'Where your files and collaboration live. Select all that apply.',
    options: [
      // Figma renders the Entra logo for Microsoft 365 too — same icon asset.
      { id: 'm365', brand: 'entra-id', label: 'Microsoft 365' },
      { id: 'google-workspace', brand: 'google-workspace', label: 'Google Workspace' },
    ],
  },
  {
    key: 'hrSystem',
    label: 'HR system',
    required: false,
    description:
      'Adds employment context (department, manager, start/leave dates). You can add this later.',
    options: [
      { id: 'hibob', brand: 'hibob', label: 'HiBob' },
      { id: 'workday', brand: 'workday', label: 'Workday' },
    ],
  },
];

/**
 * One selectable system tile (Figma `2782:8247`): a hug-width pill with a 16px
 * brand icon and 13px medium label. Selected = orange border on the warm cream
 * fill plus an orange check (`2782:8236`); disabled keeps the white pill and
 * only mutes the label, like the sign-in step's Okta row.
 */
function EnvPill({
  option,
  selected,
  disabled,
  onClick,
}: {
  option: EnvOption;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-2 self-start rounded-full border px-3 py-2 text-left',
        'font-outfit text-[13px] font-medium leading-[1.3] transition-colors',
        // #EBEBEB / #FFF7EC straight from Figma — neither sits on the grey or
        // orange token scales (see SignInStep's pill for the same gap).
        'border-[#EBEBEB] bg-dawn-white text-grey-500',
        !disabled && !selected && 'hover:border-orange-400 hover:bg-orange-100',
        selected && 'border-dawn-orange bg-[#FFF7EC] text-dawn-black',
        disabled && 'cursor-not-allowed text-grey-400'
      )}
    >
      {/* Decorative: the visible label already names the system. A full-colour
          brand icon makes a disabled pill read as enabled — desaturate it to
          match the muted label. */}
      <ConnectedServiceIcon
        service={option.brand}
        label=""
        size={16}
        className={cn(disabled && 'opacity-45 grayscale')}
      />
      <span>{option.label}</span>
      {selected && <Check className="h-3 w-3 shrink-0 text-dawn-orange" strokeWidth={3} />}
    </button>
  );
}

/** REQUIRED / OPTIONAL tag beside a section label (Figma `2764:8209` / `2764:8262`). */
function SectionTag({ required }: { required: boolean }) {
  return (
    <span
      className={cn(
        'rounded-[4px] px-1.5 py-0.5 font-outfit text-[8px] font-bold tracking-[0.4px]',
        required ? 'bg-[#FFF7EC] text-dawn-orange' : 'border border-grey-200 text-grey-400'
      )}
    >
      {required ? 'REQUIRED' : 'OPTIONAL'}
    </span>
  );
}

/**
 * What Continue hands back: one IdP, one or more workspaces (in tile order),
 * HR only if chosen.
 */
export interface DefineEnvironmentSelection<
  TIdp extends string = string,
  TWorkspace extends string = string,
  THrSystem extends string = string,
> {
  idp: TIdp;
  workspaces: TWorkspace[];
  hrSystem?: THrSystem;
}

// Generic over the three option-id types so the app parameterizes them with its
// wire enums (inferred from `onContinue`/`defaults`) — keeps this package
// app-agnostic instead of importing onboarding-domain types (shared-packages.md).
export interface DefineEnvironmentStepProps<
  TIdp extends string = string,
  TWorkspace extends string = string,
  THrSystem extends string = string,
> extends Pick<WizardPanelProps, 'onBack' | 'continueLabel' | 'frameless'> {
  /** Pre-selected systems when resuming a saved wizard (from GET /progress). */
  defaults?: Partial<DefineEnvironmentSelection<TIdp, TWorkspace, THrSystem>> | null;
  /**
   * Option ids rendered unselectable. Ids are unique across sections, so one
   * flat list covers all three groups; the app passes whatever its backend
   * can't act on yet (e.g. the HR systems).
   */
  disabledOptions?: readonly string[];
  /**
   * Fires only with both required groups picked — clicking Continue with either
   * missing shows an inline error instead.
   */
  onContinue?: (selection: DefineEnvironmentSelection<TIdp, TWorkspace, THrSystem>) => void;
}

/** Workspace tile ids in Figma order — multi-picks are emitted in this order. */
const WORKSPACE_ORDER = SECTIONS.find((s) => s.key === 'workspace')!.options.map((o) => o.id);

/**
 * Step 2 (Figma `2754:8369`) — the admin declares which systems their org runs:
 * exactly one identity provider, one or more workspaces, optionally one HR
 * system. Selection only — the actual per-system setup happens on the next
 * screen. Clicking a selected tile deselects it; required groups re-validate
 * on Continue.
 */
export function DefineEnvironmentStep<
  TIdp extends string = string,
  TWorkspace extends string = string,
  THrSystem extends string = string,
>({
  defaults,
  disabledOptions = [],
  onBack,
  onContinue,
  continueLabel,
  frameless,
}: DefineEnvironmentStepProps<TIdp, TWorkspace, THrSystem>) {
  const [picks, setPicks] = React.useState<Record<'idp' | 'hrSystem', string | null>>({
    idp: defaults?.idp ?? null,
    hrSystem: defaults?.hrSystem ?? null,
  });
  const [workspacePicks, setWorkspacePicks] = React.useState<string[]>(defaults?.workspaces ?? []);
  const [showError, setShowError] = React.useState(false);

  const toggle = (key: SectionKey, id: string) => {
    if (key === 'workspace') {
      setWorkspacePicks((prev) =>
        prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]
      );
    } else {
      setPicks((p) => ({ ...p, [key]: p[key] === id ? null : id }));
    }
    setShowError(false);
  };

  const handleContinue = () => {
    if (!picks.idp || workspacePicks.length === 0) {
      setShowError(true);
      return;
    }
    onContinue?.({
      idp: picks.idp as TIdp,
      workspaces: WORKSPACE_ORDER.filter((id) => workspacePicks.includes(id)) as TWorkspace[],
      ...(picks.hrSystem && { hrSystem: picks.hrSystem as THrSystem }),
    });
  };

  const missing = [
    !picks.idp && 'an identity provider',
    workspacePicks.length === 0 && 'a workspace',
  ]
    .filter(Boolean)
    .join(' and ');

  return (
    <WizardPanel
      variant="v2"
      title="Define your environment"
      subtitle={
        <>
          Tell us which systems you use. We&apos;ll map them and request only the <b>read-only</b>{' '}
          access each one needs in the next step.
        </>
      }
      onBack={onBack}
      onContinue={onContinue ? handleContinue : undefined}
      continueLabel={continueLabel}
      frameless={frameless}
    >
      <div className="flex flex-col gap-[22px]">
        {SECTIONS.map((section) => (
          <div key={section.key} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="font-outfit text-[11px] font-medium uppercase tracking-[0.5px] text-dawn-black">
                {section.label}
              </p>
              <SectionTag required={section.required} />
            </div>
            <p className="font-outfit text-[10px] font-normal text-grey-400">
              {section.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {section.options.map((option) => (
                <EnvPill
                  key={option.id}
                  option={option}
                  selected={
                    section.key === 'workspace'
                      ? workspacePicks.includes(option.id)
                      : picks[section.key] === option.id
                  }
                  disabled={disabledOptions.includes(option.id)}
                  onClick={() => toggle(section.key, option.id)}
                />
              ))}
            </div>
          </div>
        ))}
        {showError && (
          <p role="alert" className="text-p2 text-error">
            Choose {missing} to continue.
          </p>
        )}
      </div>
    </WizardPanel>
  );
}
